import React from "react";
import { useGetCartQuery } from "../../api/cartApi";
import Icon from "../../../../../common/components/Icon/Icon";
import { formatPrice } from "../../../../../common/helpers/formatPrice";

const SpendAndSaveProgressBar: React.FC = () => {
  const { data: cart, isLoading } = useGetCartQuery();

  if (isLoading || !cart || !cart.spendAndSavePromotion) {
    return null;
  }

  const {
    isActive,
    threshold,
    currentSubtotal,
    multiplier,
    earnedDiscount,
    nextDiscount,
    amountNeeded,
    promoTextInitial,
    promoTextProgress,
  } = cart.spendAndSavePromotion;

  if (!isActive) return null;

  const minorUnit = cart.totals.currencyMinorUnit;
  const unitMultiplier = Math.pow(10, minorUnit);

  const formattedAmountNeeded = formatPrice(
    String(amountNeeded * unitMultiplier),
    cart.totals,
  );
  const formattedEarnedDiscount = formatPrice(
    String(earnedDiscount * unitMultiplier),
    cart.totals,
  );
  const formattedNextDiscount = formatPrice(
    String(nextDiscount * unitMultiplier),
    cart.totals,
  );

  // --- INTERVAL PROGRESS MATH ---
  // The progress bar shouldn't just go to 100% and stop.
  // It should show how far we are into the *current* tier.
  // E.g. Subtotal 6000, Threshold 5000 -> 1000 into the next tier -> 20%
  const currentTierProgressAmount = currentSubtotal % threshold;
  const progressPercentage = Math.min(
    (currentTierProgressAmount / threshold) * 100,
    100,
  );

  // Determine which message to show based on multiplier
  let message = "";
  if (multiplier > 0) {
    message = promoTextProgress
      .replace("{earned_discount}", formattedEarnedDiscount)
      .replace("{next_discount}", formattedNextDiscount)
      .replace("{amount}", formattedAmountNeeded);
  } else {
    message = promoTextInitial
      .replace("{next_discount}", formattedNextDiscount)
      .replace("{amount}", formattedAmountNeeded);
  }

  // We use the 'cart-progress-bar' generic SCSS class
  // It glows green if we're technically inside a tier loop (always true unless subtotal hits exact threshold)
  // To keep it simple, we can pulse it if multiplier > 0
  const rootClassName = `cart-progress-bar spend-and-save-bar ${
    multiplier > 0 ? "is-achieved" : ""
  }`;

  return (
    <div className={rootClassName} style={{ marginBottom: "1rem" }}>
      <p className="cart-progress-bar__message">
        <Icon iconName="ticket-outlined" className="cart-progress-bar__icon" />
        {message}
      </p>
      <div className="cart-progress-bar__track">
        <div
          className="cart-progress-bar__fill"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
    </div>
  );
};

export default SpendAndSaveProgressBar;
